import { HttpErrorResponse } from '@angular/common/http';
import { translate } from '@jsverse/transloco';

/**
 * The two things every form on this app has to get right about the API.
 */

/**
 * Turns a form's empty strings into the nulls the API expects.
 *
 * An untouched text input holds '', and '' is not a valid EmailStr or date —
 * FastAPI rejects it with a 422 where the person meant "I left this blank".
 */
export function blankToNull<T extends object>(source: T): T {
  const out: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(source)) {
    out[key] = typeof value === 'string' && value.trim() === '' ? null : value;
  }
  return out as T;
}

/**
 * A stored phone number reduced to the ten digits the form now expects.
 *
 * Older records hold things like "+91 98860 00100"; loading that straight into
 * the edit form would fail validation on a field the person never touched.
 * This keeps the last ten digits — the local number without a country code.
 */
export function toPhoneDigits(value: string | null | undefined): string {
  return (value ?? '').replace(/\D/g, '').slice(-10);
}

/**
 * Whether a phone number is acceptable: blank, or exactly ten digits.
 *
 * The `input[type="tel"]` directive already stops anything but digits being
 * typed and caps the length at ten, so this only has to catch a short number
 * on submit. Returns an error sentence, or null when it is fine.
 */
export function phoneError(value: string | null | undefined): string | null {
  const v = (value ?? '').trim();
  if (v === '') {
    return null;
  }
  return /^\d{10}$/.test(v) ? null : translate('errors.phone');
}

/** One validation problem as FastAPI reports it. */
interface ValidationItem {
  loc?: (string | number)[];
  msg?: string;
}

/**
 * A sentence to show the person, whatever shape the error arrived in.
 *
 * FastAPI answers with `detail` as a string for the errors we raise ourselves,
 * but as an array of objects for schema validation failures. Reading `detail`
 * blindly puts "[object Object]" in front of the user, which says nothing about
 * what to fix.
 */
export function apiError(error: HttpErrorResponse, fallback: string): string {
  if (error.status === 0) {
    return translate('errors.offline');
  }

  const detail = error.error?.detail;

  // Some failures are nothing to do with what the person typed, and the API
  // sends the fix along with them — a schema behind its migrations, a database
  // that is not up. Showing `detail` alone says what broke and not what to do.
  const hint = error.error?.hint;

  if (typeof detail === 'string' && detail.trim()) {
    return typeof hint === 'string' && hint.trim()
      ? `${detail} ${hint.trim()}`
      : detail;
  }

  // Some refusals carry figures the screen acts on — a full section says how
  // full — so `detail` is an object with the sentence inside it.
  if (detail && typeof detail === 'object' && typeof detail.message === 'string') {
    return detail.message;
  }

  if (Array.isArray(detail) && detail.length) {
    const first = detail[0] as ValidationItem;
    // loc is like ["body", "contact_email"]; the field name is the useful part.
    const field = first.loc?.filter(p => p !== 'body').join('.');
    const message = first.msg ?? translate('errors.notValid');
    const extra =
      detail.length > 1 ? ' ' + translate('errors.andMore', { count: detail.length - 1 }) : '';
    return field ? `${field}: ${message}${extra}` : `${message}${extra}`;
  }

  return fallback;
}
